<template>

  <div class="card z-depth-0 billing-summary-surface" :class="{ 'is-submitted': cluster.status === 'p' }">
    <!-- Top -->
    <div class="billing-summary-actions" v-if="cluster.status !== 'q' && cluster?.scheme_id != 1">
      <button type="button" class="billing-export-button" @click="handleDownloadClaims"><i class="material-icons" aria-hidden="true">download</i>Claims spreadsheet</button>
      <button type="button" class="billing-export-button" @click="handleDownloadEncounters"><i class="material-icons" aria-hidden="true">download</i>Encounters spreadsheet</button>
    </div>

    <div class="billing-summary-search-frame">
      <form class="billing-member-search" role="search" @submit.prevent="handleSearchMobi">
        <i class="material-icons" aria-hidden="true">search</i>
        <input type="search" v-model="searchItem" aria-label="Search encounters by enrollee name or ID" placeholder="Search by enrollee name or ID..." />
        <button v-if="searchItem" type="button" aria-label="Clear search" @click="handleClearSearch"><i class="material-icons" aria-hidden="true">close</i></button>
      </form>

      <div class="progress billing-summary-search-loader" v-if="isLoading">
        <div class="indeterminate"></div>
      </div>
    </div>

    <!-- main  -->
    <div id="billing-summary-list" class="card z-depth-0 billing-encounter-table"
      :class="{ 'is-complete': cluster.status === 'v' && !lock }">
      <div class="card-content billing-encounter-table-surface">
        <table class="highlight responsive-table billing-encounter-summary-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Member<br /><small>(Enrollee ID)</small></th>
              <th>Diagnosis</th>
              <th>Sent Amount</th>
              <th v-if="cluster.status === 'v' && !lock">Approved Amount</th>
              <th>Encounter</th>
              <th></th>
            </tr>
          </thead>
          <tbody>

            <!-- Encounters -->
            <tr class="clickable billing-review-row" :class="{ 'is-draft': encounter._draft, 'is-pending': encounter.status === 'q' || encounter._draft }" v-for="(encounter, index) in bills" :key="encounter.id"
              @click.prevent="handleStart(encounter)" tabindex="0" @keydown.enter.prevent="handleStart(encounter)">
              <td class="billing-index">{{ index + 1 }}</td>
              <td class="billing-member">
                {{ encounter.name }}<br />
                <span class="billing-member-id">({{ encounter.enrollee_number }})</span>
              </td>
              <td class="billing-diagnosis">{{ encounter.diagnosis }}</td>
              <td class="billing-amount-cell">
                ₦{{ totalSent(encounter) }}
              </td>
              <td class="billing-amount-cell is-approved" v-if="cluster.status === 'v' && !lock">
                ₦{{ totalApproved(encounter) }}
              </td>
              <td class="billing-date">
                {{ billingDate(encounter.encounter_date) }}
              </td>
              <td class="billing-status">
                <i class="material-icons billing-claim-status" :class="{ 'is-pending': encounter.status === 'q' || encounter._draft }" :title="encounter.status === 'q' || encounter._draft ? 'Pending' : 'Ready'" :aria-label="encounter.status === 'q' || encounter._draft ? 'Pending' : 'Ready'">{{ encounter.status === 'q' || encounter._draft ? 'watch_later' : 'check_circle' }}</i>
              </td>
            </tr>

            <!-- Total -->
            <tr class="billing-total-strip" v-if="bills.length">
              <td colspan="2"></td>
              <td class="billing-total-label">Total Sent:</td>
              <td class="red-text text-darken-2"><b>₦{{ grandTotalSent(bills) }}</b></td>
              <td class="green-text text-darken-2" v-if="cluster.status === 'v' && !lock"><b>₦{{
                grandTotalApproved(bills) }}</b></td>
              <td colspan="2"></td>
            </tr>

            <!-- No encounters yet -->
            <tr v-else>
              <td colspan="6">
                <div class="billing-empty-state">
                  <i class="material-icons">inbox</i>
                  <span>No encounters have been added to this billing batch.</span>
                </div>
              </td>
            </tr>

          </tbody>
        </table>
      </div>
    </div>

  </div>

  <!-- Loader -->
  <div class="progress" v-if="isSubmitting">
    <div class="indeterminate"></div>
  </div>

</template>

<script>
import { computed, ref, watchEffect, watch } from "vue";
import { billingText, billingDate } from "./billingData";
import {
  totalSentInputFx,
  grossTotalSentInputFx,
  totalApprovedFx,
  grossTotalApprovedFx,
} from "@/composables/calFunction/cal";
import {
  downloadExcelFx,
} from "@/composables/reportFunction/downloader";


export default {
  name: "SummaryClaimComponent",
  props: ["cluster", "encounters", "isSubmitting", "isManual"],
  emits: ["cancel", "reload", "restart"],
  setup(props, context) {
    const lock = ref(true);
    const isLoading = ref(false);
    const response = ref(null);
    const searchItem = ref(null);
    const search = ref(false);

    watch(
      () => props.encounters,
      (newVal) => {
        response.value = [...(newVal || [])];
      },
      { immediate: true },
    );

    // total calculations
    const totalSent = (encounter) => {
      return totalSentInputFx(encounter);
    }

    // total calculations
    const grandTotalSent = (encounter) => {
      return grossTotalSentInputFx(encounter);
    }

    // total approved
    const totalApproved = (encounter) => {
      return totalApprovedFx(encounter);
    }

    // grand total approved
    const grandTotalApproved = (encounter) => {
      return grossTotalApprovedFx(encounter);
    }

    // switch page
    const handleStart = (encounter) => {
      // reload
      context.emit("reload", {
        encounter: encounter,
      });
    };

    // restart (clear)
    const handleRestart = () => {
      context.emit("restart", true);
    }

    // default
    const sortDefault = () => {
      if (response.value) {
        // start
        searchItem.value = null;
        // Sort cby pending
        response.value = [...(props.encounters || [])];
      }
    }

    // searcher
    const sortSearch = () => {
      if (response.value) {
        // sort
        response.value.sort((a, b) => {
          const searchValue = billingText(searchItem.value).toLowerCase();
          const matchesA = billingText(a.name).toLowerCase().includes(searchValue) ||
            billingText(a.enrollee_number).toLowerCase().includes(searchValue);
          const matchesB = billingText(b.name).toLowerCase().includes(searchValue) ||
            billingText(b.enrollee_number).toLowerCase().includes(searchValue);

          if (matchesA && !matchesB) {
            return -1; // a comes first
          } else if (!matchesA && matchesB) {
            return 1;  // b comes first
          } else {
            return 0;  // no change in order
          }

        });

        // start loading
        isLoading.value = true;
        // end loading
        setTimeout(() => {
         isLoading.value = false;
        }, 2000);

      }
    }

    // restart
    const handleClearSearch = () => {
      //searchItem.value = null;
      sortDefault();
    };

    // search mobi
    const handleSearchMobi = () => {
      if (searchItem.value) {
        // search
        sortSearch();
      }
    }

    // search watcher
    watchEffect(() => {
      if (searchItem.value) {
        // search
        sortSearch();
      } else {
        sortDefault();
      }
    });

    // cancel
    const handleCancel = () => {
      context.emit("cancel", true);
    }

    // output results
    const bills = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    // download claims
    const handleDownloadClaims = () => {
      let data = [];
      let filteredClaims = [];


      // total
      props.encounters.forEach((x, y) => {
        // clear
        filteredClaims = [];
        // filter 
        filteredClaims = x.claims.filter(claim => !(claim.sent_price == "0.00" && claim.approved_price == "0.00"));

        // counter
        if (filteredClaims.length) {

          // detail
          x.claims.forEach((z, j) => {

            if (j === 0) {

              // first
              data.push({
                No: y + 1,
                Name: x.name,
                ID: x.enrollee_number,
                Encounter: x.encounter_date,
                End: x.encounter_date_end,
                Diagnosis: x.diagnosis,
                Item: z.item,
                Sent_Qty: z.sent_qty,
                Sent: z.sent_price,
                Authorization: z.authorization == 'y' ? z.authorization_code : '',
              })

            } else {

              // others
              data.push({
                No: "",
                Name: "",
                ID: "",
                Encounter: "",
                End: "",
                Diagnosis: "",
                Item: z.item,
                Sent_Qty: z.sent_qty,
                Sent: z.sent_price,
                Authorization: z.authorization == 'y' ? z.authorization_code : '',
              })

            }

          })

        }

        // total
        data.push({
          No: "",
          Name: "",
          ID: "",
          Encounter: "",
          End: "",
          Diagnosis: "",
          Item: "",
          Sent_Qty: "Subtotal",
          Sent: totalSent(x),
          Authorization: "",
        })

        // space
        data.push({})

      });

      // space
      data.push({})

      // final
      data.push({
        No: "",
        Name: "",
        ID: "",
        Encounter: "",
        End: "",
        Diagnosis: "",
        Item: "",
        Sent_Qty: "Grand Total",
        Sent: grandTotalSent(props.encounters),
        Authorization: "",
      })

      // fire function
      return downloadExcelFx(data, `${props.cluster.provider?.name || "Provider"}_${props.cluster.scheme?.name || "Scheme"}_${props.cluster.month.name}_${props.cluster.year.name}_Claims`);
    }

    // download encounters
    const handleDownloadEncounters = () => {
      let data = [];

      // total
      props.encounters.forEach((x, y) => {

        // detail
        x.claims.forEach((z, j) => {

          if (j === 0) {

            // first
            data.push({
              No: y + 1,
              Name: x.name,
              ID: x.enrollee_number,
              Age: x?.stores?.age ? x?.stores?.age : "",
              Sex: x.sex == "m" ? "Male" : "Female",
              Type: x.type == "p" ? "Principal" : "Dependant",
              Encounter: x.encounter_date,
              End: x.encounter_date_end,
              Referral: x.provider_id != x.picked_provider_id ? "Referred" : "Nil" ,
              Hospitalization: x.encounter_date != x.encounter_date_end ? "Admission" : "Outpatient",
              Diagnosis: x.diagnosis,
              Item: z.item,
              Sent_Qty: z.sent_qty,
              Doctor: x?.stores?.doctor ? x?.stores?.doctor : "",
            })

          } else {

            // others
            data.push({
              No: "",
              Name: "",
              ID: "",
              Age: "",
              Sex: "",
              Type: "",
              Encounter: "",
              End: "",
              Referral: "",
              Hospitalization: "",
              Diagnosis: "",
              Item: z.item,
              Sent_Qty: z.sent_qty,
              Doctor: "",
            })

          }

        })

        // total
        data.push({
          No: "",
          Name: "",
          ID: "",
          Age: "",
          Sex: "",
          Type: "",
          Encounter: "",
          End: "",
          Referral: "",
          Hospitalization: "",
          Diagnosis: "",
          Item: "",
          Sent_Qty: "",
          Doctor: "",
        })

        // space
        data.push({})

      });

      // space
      data.push({})

      // final
      data.push({
        No: "",
        Name: "",
        ID: "",
        Age: "",
        Sex: "",
        Type: "",
        Encounter: "",
        End: "",
        Referral: "",
        Hospitalization: "",
        Diagnosis: "",
        Item: "",
        Sent_Qty: "",
        Doctor: "",
      })

      // fire function
      return downloadExcelFx(data, `${props.cluster.provider?.name || "Provider"}_${props.cluster.scheme?.name || "Scheme"}_${props.cluster.month.name}_${props.cluster.year.name}_Encounters`);
    }

    return {
      billingDate,
      lock,
      handleSearchMobi,
      handleClearSearch,
      handleDownloadClaims,
      handleDownloadEncounters,
      handleCancel,
      handleRestart,
      handleStart,
      search,
      searchItem,
      isLoading,
      grandTotalSent,
      totalSent,
      totalApproved,
      grandTotalApproved,
      bills,
    };
  },
};
</script>

<style scoped>
@layer components {
  .billing-summary-surface .billing-row-status { align-items: center !important; background: #edf9f1 !important; border: 1px solid #bfe2cb !important; border-radius: 999px !important; color: #16804a !important; display: inline-flex !important; font-size: .6875rem !important; font-weight: 600 !important; gap: .3rem !important; padding: .3rem .55rem !important; white-space: nowrap !important; }
  .billing-summary-surface .billing-row-status.is-pending { background: #fff7df !important; border-color: #eed28b !important; color: #946200 !important; }
  .billing-summary-surface .billing-row-status i { color: inherit !important; font-size: .875rem !important; }
  .billing-summary-surface .billing-encounter-summary-table .billing-amount-cell { color: #0f766e !important; }
  .billing-summary-surface .billing-encounter-summary-table .billing-total-strip { background: #f0f9f6 !important; }
  .billing-summary-surface .billing-encounter-summary-table .billing-total-strip td { color: #0f766e !important; }
  .billing-summary-surface .billing-summary-search-frame .billing-member-search > i { color: #0f766e !important; }
  .billing-summary-surface .billing-member-search input[type="search"] { border: 0 !important; border-radius: 0 !important; box-shadow: none !important; }
  .billing-summary-surface.card { background: transparent !important; border: 0 !important; box-shadow: none !important; gap: 1.25rem !important; margin: 0 !important; padding: 0 !important; }
  .billing-summary-surface .billing-summary-actions { background: transparent !important; border: 0 !important; display: flex !important; flex-wrap: wrap !important; gap: .75rem !important; margin: 0 !important; padding: 0 !important; }
  .billing-summary-surface .billing-export-button { align-items: center !important; background: var(--ui-background) !important; border: 1px solid var(--ui-border) !important; border-radius: calc(var(--ui-radius) - 2px) !important; color: var(--ui-foreground) !important; cursor: pointer !important; display: inline-flex !important; font: inherit !important; font-size: .8125rem !important; font-weight: 600 !important; gap: .5rem !important; min-height: 2.5rem !important; padding: 0 .9rem !important; }
  .billing-summary-surface .billing-export-button i { font-size: 1rem !important; }
  .billing-summary-surface .billing-summary-search-frame { background: transparent !important; border: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
  .billing-summary-surface .billing-member-search { align-items: center !important; background: var(--ui-background) !important; border: 1px solid var(--ui-input) !important; border-radius: calc(var(--ui-radius) - 2px) !important; display: flex !important; gap: .65rem !important; margin: 0 !important; max-width: 34rem !important; min-height: 2.75rem !important; padding: 0 .75rem !important; }
  .billing-summary-surface .billing-member-search > i { color: var(--ui-muted-foreground) !important; font-size: 1.1rem !important; }
  .billing-summary-surface .billing-member-search input { background: transparent !important; border: 0 !important; box-shadow: none !important; color: var(--ui-foreground) !important; flex: 1 !important; font: inherit !important; font-size: .875rem !important; height: 2.75rem !important; margin: 0 !important; min-width: 0 !important; outline: 0 !important; padding: 0 !important; width: 100% !important; }
  .billing-summary-surface .billing-member-search input::-webkit-search-cancel-button { -webkit-appearance: none !important; }
  .billing-summary-surface .billing-member-search button { align-items: center !important; background: transparent !important; border: 0 !important; color: var(--ui-muted-foreground) !important; cursor: pointer !important; display: inline-flex !important; padding: .25rem !important; }
  .billing-summary-surface .billing-member-search button i { font-size: 1rem !important; }
  .billing-summary-surface .billing-member-search:focus-within { outline: 2px solid var(--ui-ring) !important; outline-offset: 2px !important; }
  .billing-summary-surface .billing-encounter-table.card { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; overflow-x: auto !important; padding: 0 !important; }
  .billing-summary-surface .billing-encounter-table-surface.card-content { background: transparent !important; border: 0 !important; margin: 0 !important; padding: 0 !important; }
  .billing-summary-surface table.billing-encounter-summary-table { border: 0 !important; border-collapse: collapse !important; display: table !important; font-size: .8125rem !important; min-width: 46rem !important; width: 100% !important; }
  .billing-summary-surface .billing-encounter-summary-table thead { background: transparent !important; display: table-header-group !important; float: none !important; }
  .billing-summary-surface .billing-encounter-summary-table tbody { display: table-row-group !important; overflow: visible !important; white-space: normal !important; }
  .billing-summary-surface .billing-encounter-summary-table tr { background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; box-shadow: none !important; display: table-row !important; padding: 0 !important; }
  .billing-summary-surface .billing-encounter-summary-table :is(th, td) { border: 0 !important; display: table-cell !important; float: none !important; padding: 1.15rem .75rem !important; text-align: left !important; vertical-align: middle !important; }
  .billing-summary-surface .billing-encounter-summary-table th { color: var(--ui-muted-foreground) !important; font-size: .75rem !important; font-weight: 600 !important; padding-block: .8rem !important; text-transform: uppercase !important; }
  .billing-summary-surface .billing-encounter-summary-table thead th:first-child,
  .billing-summary-surface .billing-encounter-summary-table td.billing-index { padding-left: 1.5rem !important; }
  .billing-summary-surface .billing-encounter-summary-table td { color: var(--ui-foreground) !important; font-size: .8125rem !important; }
  .billing-summary-surface .billing-review-row:is(:hover, :focus-visible) { background: var(--ui-muted) !important; outline: 0 !important; }
  .billing-summary-surface .billing-member { font-weight: 600 !important; }
  .billing-summary-surface .billing-member-id { color: var(--ui-muted-foreground) !important; font-size: .75rem !important; font-weight: 400 !important; }
  .billing-summary-surface .billing-amount-cell { font-weight: 600 !important; white-space: nowrap !important; }
  .billing-summary-surface .billing-total-strip { background: var(--ui-muted) !important; }
  .billing-summary-surface .billing-draft-badge { display: none !important; }
  .billing-summary-surface .billing-status i { font-size: 1.125rem !important; }
  .billing-summary-surface button:focus-visible { outline: 2px solid var(--ui-ring) !important; outline-offset: 2px !important; }
}
.progress {
  margin-top: -2px;
}

.billing-summary-surface {
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: transparent;
  box-shadow: none;
}

.billing-summary-actions {
  border: 1px solid #d6e5ea;
  border-radius: 0;
  background: #f8fbfb;
  padding: 14px;
}

.billing-summary-surface.is-submitted .billing-summary-actions {
  background: #f8fbfb;
  border-color: #d6e5ea;
}

.billing-summary-actions .row {
  margin-bottom: 0;
}

.billing-download-tile {
  min-height: 82px;
  border: 1px solid #cfe3e4;
  border-radius: 0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: border-color 0.2s ease;
}

.billing-summary-surface.is-submitted .billing-download-tile {
  background: #ffffff;
  border-color: #cfe3e4;
}

.billing-download-tile:hover {
  border-color: #00897b;
  box-shadow: none;
  transform: none;
}

.billing-download-tile h5 {
  font-size: 1rem;
  line-height: 1.3;
  margin: 0;
  width: 100%;
}

.billing-download-tile a {
  color: #263238;
  display: flex;
  align-items: center;
  gap: 12px;
}

.billing-download-tile i {
  width: 44px;
  height: 44px;
  border-radius: 0;
  background: #e8f5f5;
  color: #00897b !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
}

.billing-summary-surface.is-submitted .billing-download-tile i {
  background: #c8f2e9;
  color: #00796b !important;
}

.billing-download-tile.is-secondary i {
  background: #eef2f6;
  color: #37474f !important;
}

.billing-summary-surface.is-submitted .billing-download-tile.is-secondary i {
  background: #d9f0ff;
  color: #1565c0 !important;
}

.billing-download-tile small {
  color: #607d8b;
  font-size: 0.78rem;
  font-weight: 500;
}

.billing-summary-search-frame {
  position: relative;
}

.billing-summary-search {
  background: #ffffff !important;
  border: 1px solid #c4d3d9;
  border-radius: 0;
  padding: 0 12px;
  min-height: 58px;
  overflow: hidden;
}

.billing-summary-search .nav-wrapper,
.billing-summary-search form,
.billing-summary-search .input-field {
  min-height: 58px;
  height: 58px;
  margin: 0;
}

.billing-summary-search input[type="search"] {
  border-bottom: 0 !important;
  box-shadow: none !important;
  color: #263238;
  margin: 0;
}

.billing-summary-search .label-icon i {
  color: #00897b !important;
}

.billing-summary-search-loader {
  border-radius: 0;
  bottom: 0;
  height: 3px;
  left: 1px;
  margin: 0;
  overflow: hidden;
  position: absolute;
  right: 1px;
  z-index: 2;
}

.billing-encounter-table {
  border: 1px solid #c4d3d9;
  border-radius: 0;
  background: #fff;
  box-shadow: none;
  overflow-x: auto;
  scroll-margin-top: 104px;
}

.billing-encounter-table .card-content {
  padding: 16px;
}

.billing-encounter-table-surface {
  background: #ffffff;
}

.billing-encounter-summary-table {
  border-collapse: separate;
  border-spacing: 0 10px;
  margin: 0;
}

.billing-encounter-summary-table thead tr {
  background: linear-gradient(90deg, #f7fcfb 0%, #fffaf0 100%);
}

.billing-encounter-summary-table th {
  border-bottom: 1px solid #c4d3d9;
  color: #4f7380;
  font-size: 0.76rem;
  letter-spacing: 0;
  text-transform: uppercase;
  padding: 0 16px 6px;
}

.billing-encounter-summary-table th:nth-child(2) {
  color: #00695c;
}

.billing-encounter-summary-table th:nth-child(3) {
  color: #795000;
}

.billing-encounter-summary-table th:nth-child(4) {
  color: #bf360c;
}

.billing-encounter-summary-table th small {
  color: #8aa0aa;
  font-size: 0.72rem;
  text-transform: none;
}

.billing-review-row td {
  background: #ffffff;
  border-top: 1px solid #dce8ec;
  border-bottom: 1px solid #dce8ec;
  color: #263238;
  padding: 18px 16px;
  vertical-align: middle;
}

.billing-review-row td:first-child {
  color: #00897b;
  font-weight: 800;
}

.billing-review-row td:first-child {
  border-left: 1px solid #dce8ec;
  border-radius: 0;
}

.billing-review-row td:last-child {
  border-right: 1px solid #dce8ec;
  border-radius: 0;
}

.billing-review-row:hover td {
  background: #f7fcfb;
}

.billing-review-row.is-draft td {
  background: #fffdf5;
  border-color: #eadca9;
}

.billing-review-row.is-draft:hover td {
  background: #fff8df;
}

.billing-member,
.billing-diagnosis {
  font-weight: 700;
}

.billing-member {
  color: #263238;
}

.billing-member-id {
  font-weight: 400;
}

.billing-diagnosis {
  color: #4e342e;
}

.billing-member small,
.billing-date {
  color: #607d8b;
  font-weight: 500;
}

.billing-amount-cell {
  font-weight: 700;
  color: #bf360c;
}

.billing-amount-cell.is-approved {
  color: #2e7d32;
}

.billing-status {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}

.billing-draft-badge {
  background: #fff4d6;
  border: 1px solid #f1c75b;
  color: #795000;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  padding: 7px 9px;
  text-transform: uppercase;
}

.billing-total-strip td {
  background: #f4f7f8;
  border-top: 1px solid #dce8ec;
  border-bottom: 1px solid #dce8ec;
  padding: 16px;
}

.billing-total-strip td:first-child {
  border-left: 1px solid #dce8ec;
  border-radius: 0;
}

.billing-total-strip td:last-child {
  border-right: 1px solid #dce8ec;
  border-radius: 0;
}

.billing-total-label {
  color: #4f7380;
  font-weight: 700;
}

.billing-empty-state {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #607d8b;
}

.billing-empty-state i {
  color: #00897b;
}

.custom-hr {
  width: 50%;
  /* Adjust the width as needed */
  border: none;
  border-top: 2px solid #ccc;
  /* Adjust the color and style as needed */
  margin: 20px 20px;
  /* Adjust margin as needed */
}

.styled-text {
  background-color: #fafad2;
  /* Light Golden Yellow */
  /* border: 2px solid #000; Black border */
  padding: 2px;
  /* Adequate padding */
}

.capsule {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 20px;
  background-color: #f0f0f0;
  /* Adjust the background color as needed */
}

.clickable {
  cursor: pointer;
}

.pointable {
  cursor: default;
  opacity: 1;
  /* Default opacity */
  transition: opacity 0.3s ease;
  /* Smooth opacity change */
}

.pointable:hover {
  opacity: 0.8;
  /* Slightly reduced opacity on hover */
}

@media screen and (max-width: 720px) {
  .billing-summary-actions {
    padding: 10px;
  }

  .billing-download-tile {
    margin-bottom: 10px;
  }

  .billing-encounter-table .card-content {
    padding: 10px;
  }

  .billing-review-row td,
  .billing-total-strip td {
    padding: 14px 12px;
  }

}

/* Match the claim detail register while keeping provider billing columns. */
@layer components {
.billing-summary-surface.card .billing-encounter-table.card { background: #fff !important; border: 1px solid #c4d3d9 !important; border-radius: 6px !important; }
.billing-summary-surface.card .billing-encounter-summary-table thead { background: #f8fafb !important; }
.billing-summary-surface.card .billing-encounter-summary-table th { color: #546e7a !important; font-size: .78rem !important; font-weight: 800 !important; padding: 14px 16px !important; }
.billing-summary-surface.card .billing-encounter-summary-table td { color: #263238 !important; font-size: 1rem !important; font-weight: 400 !important; line-height: 1.35 !important; padding: 15px 16px !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-member-id { color: #263238 !important; font-size: .84rem !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-index { color: #455a64 !important; font-weight: 700 !important; padding-left: 1.5rem !important; width: 54px !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-review-row.is-pending { background: #fffdf7 !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-review-row:is(:hover, :focus-visible) { background: #f8fafb !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-total-strip { background: #f8fafc !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-total-strip td { color: #263238 !important; font-weight: 800 !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-total-strip td:nth-child(4) { color: #ed1231 !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-status .billing-claim-status { align-items: center !important; background: #f7fafb !important; border: 1px solid #e1e8eb !important; border-radius: 50% !important; color: #78909c !important; display: inline-flex !important; font-size: 17px !important; height: 28px !important; justify-content: center !important; width: 28px !important; }
.billing-summary-surface.card .billing-encounter-summary-table .billing-status .billing-claim-status.is-pending { background: #fffaf0 !important; border-color: #f2dfb3 !important; color: #a8741a !important; }
}

</style>
